import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  addDecedent,
  editDecedent,
  editUserPersona,
  getDecedent,
  getDecedentList,
  getTaskList,
  submitQuestion,
} from '@/entities/decedent/decedent.api';
import { decedentQueryKeys } from '@/entities/decedent/decedent.keys';
import { TASK_MODULES } from '@/common/constants/constants';

export const useDecedentListQuery = (options = {}) =>
  useQuery({
    queryKey: decedentQueryKeys.list,
    queryFn: getDecedentList,
    ...options,
  });

export const useDecedentQuery = (decedentId, options = {}) =>
  useQuery({
    queryKey: decedentQueryKeys.detail(decedentId),
    queryFn: () => getDecedent(decedentId),
    enabled: !!decedentId,
    ...options,
  });

export const useTaskListQuery = (decedentId, module, options = {}) =>
  useQuery({
    queryKey: decedentQueryKeys.taskList(decedentId, module),
    queryFn: () => getTaskList(decedentId, module),
    enabled: !!decedentId && !!module,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    ...options,
  });

export const useTaskListsQuery = (
  decedentId,
  modules = Object.values(TASK_MODULES),
  options = {}
) =>
  useQueries({
    queries: modules.map((module) => ({
      queryKey: decedentQueryKeys.taskList(decedentId, module),
      queryFn: () => getTaskList(decedentId, module),
      enabled: !!decedentId && !!module,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      ...options,
    })),
  });

const invalidateDecedent = (queryClient, decedentId) =>
  queryClient.invalidateQueries({
    queryKey: decedentQueryKeys.detail(decedentId),
  });

const invalidateTaskList = (queryClient, decedentId, module) =>
  queryClient.invalidateQueries({
    queryKey: decedentQueryKeys.taskList(decedentId, module),
  });

const invalidateAllTaskLists = (queryClient, decedentId) =>
  queryClient.invalidateQueries({
    queryKey: decedentQueryKeys.taskLists(decedentId),
  });

export const useAddDecedentMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: addDecedent,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({ queryKey: decedentQueryKeys.list });
      await onSuccess?.(...args);
    },
    ...mutationOptions,
  });
};

export const useEditDecedentMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ id, form }) => editDecedent(id, form),
    onSuccess: async (data, variables, context) => {
      await invalidateDecedent(queryClient, variables.id);
      await onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
};

export const useEditUserPersonaMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ id, form }) => editUserPersona(id, form),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: decedentQueryKeys.details,
      });
      await onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
};

export const useSubmitQuestionMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: submitQuestion,
    onSuccess: async (data, variables, context) => {
      const { decedentId, section } = variables;

      if (
        [TASK_MODULES.IMMEDIATE, 'basic_document_submission'].includes(section)
      ) {
        await invalidateDecedent(queryClient, decedentId);
      }

      if (Object.values(TASK_MODULES).includes(section)) {
        await invalidateTaskList(queryClient, decedentId, section);
        await invalidateDecedent(queryClient, decedentId);
      }

      await onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
};

export const useInvalidateDecedentQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateDecedent: (decedentId) =>
      invalidateDecedent(queryClient, decedentId),
    invalidateTaskList: (decedentId, module) =>
      invalidateTaskList(queryClient, decedentId, module),
    invalidateAllTaskLists: (decedentId) =>
      invalidateAllTaskLists(queryClient, decedentId),
    resetDecedentQueries: () =>
      queryClient.removeQueries({ queryKey: decedentQueryKeys.all }),
  };
};
